import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { getSearchAPI } from "@/redux/Purchase/actions";
import { useDispatch } from "react-redux";
import "./SearchBar.css";
import { useDebounced } from "@/hooks/useDebounced";
import FormModalShell from "../ui/FormModalShell";

export const SearchBar = ({
  selectReacord,
  searchAPI,
  value,
  inputStyle = "",
  minword = 2,
  showResults = 1,
  onchange = () => { },
  paramCompanyId = "CompanyId",
  enableKeyboardNavigation = true,
  clearOnBlurWhenUnselected = false,
  preventClipboard = false,
  name,
  hasError = false,
  placeholder = "Type to search...",
  className = "",
}) => {
  // Always a string: the parent's value is often undefined on first render,
  // which would make the input uncontrolled until the form data loads.
  const [input, setInput] = useState(value ?? "");
  const [searchResult, setSearchResult] = useState([]);
  const [searchResultRaw, setSearchResultRaw] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // console.log("searchResult: ", searchResult);
  const [dropdownPos, setDropdownPos] = useState(null);
  // console.log("dropdownPos: ", dropdownPos);
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const optionRefs = useRef([]);
  const hasUnselectedInputRef = useRef(false);
  const glowTimeoutRef = useRef(null);
  const [unselectedError, setUnselectedError] = useState(false);
  const [unselectedGlow, setUnselectedGlow] = useState(false);
  const [MasterCompanyDetails] = JSON.parse(
    localStorage.getItem("MasterCompanyDetails") ?? "[{}]",
  );

  useEffect(() => {
    setInput(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (glowTimeoutRef.current) window.clearTimeout(glowTimeoutRef.current);
    };
  }, []);
  const updatePosition = () => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    setDropdownPos({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  useEffect(() => {
    if (searchResult.length) updatePosition();
  }, [searchResult]);
  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setSearchResult([]);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async (value) => {
    if (!value) {
      setSearchResult([]);
      setSearchResultRaw([]);
      return;
    }
    const URL =
      searchAPI +
      value +
      `&${paramCompanyId}=${MasterCompanyDetails?.CompanyId}`;
    // console.log("URL", URL);
    const sdata = await dispatch(getSearchAPI(URL));
    const rows = Array.isArray(sdata) ? sdata : [];
    const result = rows.map((obj) => Object.values(obj));
    setSearchResult(result);
    setSearchResultRaw(rows);
  };

  const fetch = useDebounced(fetchData, 300);

  const handleChange = (value) => {
    hasUnselectedInputRef.current = true;
    setUnselectedError(false);
    setUnselectedGlow(false);
    if (glowTimeoutRef.current) {
      window.clearTimeout(glowTimeoutRef.current);
      glowTimeoutRef.current = null;
    }
    setInput(value);
    setHighlightedIndex(-1);
    onchange(value);
    if (value?.length > minword) {
      fetch(value);
    } else {
      setSearchResult([]);
      setSearchResultRaw([]);
    }
  };

  const selectResult = (result, raw) => {
    hasUnselectedInputRef.current = false;
    setUnselectedError(false);
    setUnselectedGlow(false);
    if (glowTimeoutRef.current) {
      window.clearTimeout(glowTimeoutRef.current);
      glowTimeoutRef.current = null;
    }
    selectReacord(result, raw);
    setInput(result?.[1] ?? "");
    setSearchResult([]);
    setSearchResultRaw([]);
    setHighlightedIndex(-1);
  };

  const handleBlur = () => {
    if (!clearOnBlurWhenUnselected) return;

    // A result click happens after the input blur event. Defer this check so
    // selectResult can mark the typed value as a valid selection first.
    setTimeout(() => {
      if (!hasUnselectedInputRef.current) return;
      hasUnselectedInputRef.current = false;
      setInput("");
      setSearchResult([]);
      setHighlightedIndex(-1);
      onchange("");
      setUnselectedError(true);
      setUnselectedGlow(true);
      toast.error("You did not select any option.");
      if (glowTimeoutRef.current) window.clearTimeout(glowTimeoutRef.current);
      glowTimeoutRef.current = window.setTimeout(() => {
        setUnselectedGlow(false);
        glowTimeoutRef.current = null;
      }, 650);
    }, 0);
  };

  const handleKeyDown = (event) => {
    if (!enableKeyboardNavigation || !searchResult.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) =>
        current < searchResult.length - 1 ? current + 1 : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) =>
        current > 0 ? current - 1 : searchResult.length - 1,
      );
    } else if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      selectResult(
        searchResult[highlightedIndex],
        searchResultRaw[highlightedIndex],
      );
    }
  };

  useEffect(() => {
    if (!enableKeyboardNavigation || highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [enableKeyboardNavigation, highlightedIndex]);

  const dropdown =
    searchResult.length > 0 && dropdownPos ? (
      <div
        style={{
          position: "absolute",
          top: dropdownPos.top + 2,
          left: dropdownPos.left,
          width: dropdownPos.width,
          zIndex: 99999,
        }}
        className="border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white max-h-[150px] overflow-y-auto shadow-lg pointer-events-auto"
      >
        {searchResult.map((result, id) => (
          <div
            key={id}
            ref={(element) => {
              optionRefs.current[id] = element;
            }}
            className={`cursor-pointer py-1 px-2 text-[14px] text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700 ${enableKeyboardNavigation && highlightedIndex === id
                ? "bg-slate-100 dark:bg-slate-700"
                : ""
              }`}
            onMouseDown={(e) => {
              // Select before the input's blur handler runs. Preventing the
              // default keeps focus on the input until selection is committed.
              e.preventDefault();
              e.stopPropagation();
              selectResult(result, searchResultRaw[id]);
            }}
            onClick={(e) => {
              handleClickOutside(e);
            }}
          >
            {result[showResults]}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <>
      <div className="relative" ref={wrapperRef}>
        <input
          name={name}
          placeholder={placeholder}
          value={input ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-invalid={hasError || unselectedError || undefined}
          onCut={preventClipboard ? (event) => event.preventDefault() : undefined}
          onCopy={preventClipboard ? (event) => event.preventDefault() : undefined}
          onPaste={preventClipboard ? (event) => event.preventDefault() : undefined}
          className={
            "w-full px-3 py-2 text-sm border rounded-lg " +
            (className ? `${className} ` : "") +
            (inputStyle || "") +
            // Force readable colors so callers can't make light-mode text invisible
            " !bg-white dark:!bg-slate-800 !text-slate-900 dark:!text-white " +
            "placeholder:text-slate-400 dark:placeholder:text-slate-500 " +
            "focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF] " +
            (hasError
              ? "!border-red-500 dark:!border-red-500 "
              : unselectedGlow
                ? "searchbar-unselected-glow "
                : unselectedError
                  ? "searchbar-unselected-solid "
                  : "border-slate-300 dark:border-slate-700 ")
          }
        />
      </div>

      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
};
